import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import API from '../api';

const PAGE_SIZE = 10;

const PatientsList = () => {
    const [patients, setPatients] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [searchData, setSearchData] = useState({ firstName: '', lastName: '', phoneNumber: '' });
    const [appliedSearch, setAppliedSearch] = useState({ firstName: '', lastName: '', phoneNumber: '' });
    const [showForm, setShowForm] = useState(false);
    const [newPatient, setNewPatient] = useState({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        birthDate: '',
        gender: ''
    });
    const [editingPatient, setEditingPatient] = useState(null);
    const [editForm, setEditForm] = useState({ firstName: '', lastName: '', phoneNumber: '', birthDate: '', gender: '' });
    const [savingEdit, setSavingEdit] = useState(false);

    const navigate = useNavigate();

    const fetchPage = async (pageNumber, search) => {
        try {
            const res = await API.get('/patients/paged', {
                params: { ...search, page: pageNumber, size: PAGE_SIZE }
            });
            setPatients(res.data.content);
            setTotalPages(res.data.totalPages);
            setPage(res.data.number);
        } catch (err) {
            console.error("Greška pri učitavanju pacijenata", err);
        }
    };

    useEffect(() => {
        fetchPage(0, appliedSearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearchChange = (e) => {
        setSearchData({ ...searchData, [e.target.name]: e.target.value });
    };

    const executeSearch = () => {
        setAppliedSearch(searchData);
        fetchPage(0, searchData);
    };

    const goToPage = (pageNumber) => {
        fetchPage(pageNumber, appliedSearch);
    };

    const handleInputChange = (e) => {
        setNewPatient({ ...newPatient, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // send null instead of '' - backend rejects empty string for LocalDate/enum fields
            await API.post('/patients', { ...newPatient, birthDate: newPatient.birthDate || null, gender: newPatient.gender || null });
            setShowForm(false);
            setNewPatient({ firstName: '', lastName: '', phoneNumber: '', birthDate: '', gender: '' });
            fetchPage(0, appliedSearch);
        } catch (err) {
            alert(err.response?.data?.message || "Greška pri čuvanju pacijenta!");
        }
    };

    const handleRowClick = (patientId) => {
        navigate(`/group-scheduling?patientId=${patientId}`);
    };

    const openEdit = (e, patient) => {
        e.stopPropagation();
        setEditingPatient(patient);
        setEditForm({
            firstName: patient.firstName || '',
            lastName: patient.lastName || '',
            phoneNumber: patient.phoneNumber || '',
            birthDate: patient.birthDate || '',
            gender: patient.gender || ''
        });
    };

    const closeEdit = () => {
        setEditingPatient(null);
    };

    const handleEditChange = (e) => {
        setEditForm({ ...editForm, [e.target.name]: e.target.value });
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        setSavingEdit(true);
        try {
            await API.put(`/patients/${editingPatient.id}`, { ...editForm, birthDate: editForm.birthDate || null, gender: editForm.gender || null });
            setEditingPatient(null);
            fetchPage(page, appliedSearch);
        } catch (err) {
            alert(err.response?.data?.message || "Greška pri čuvanju izmena!");
        } finally {
            setSavingEdit(false);
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2 style={styles.title}>Pacijenti</h2>
                <button
                    style={styles.addBtn}
                    onClick={() => setShowForm(!showForm)}
                >
                    {showForm ? 'Zatvori' : '+ Novi'}
                </button>
            </div>

            {/* Panel za pretragu sa 3 polja */}
            <div style={styles.searchPanel}>
                <input name="firstName" placeholder="Ime" style={styles.miniInput} value={searchData.firstName} onChange={handleSearchChange} />
                <input name="lastName" placeholder="Prezime" style={styles.miniInput} value={searchData.lastName} onChange={handleSearchChange} />
                <input name="phoneNumber" placeholder="Telefon" style={styles.miniInput} value={searchData.phoneNumber} onChange={handleSearchChange} />
                <button onClick={executeSearch} style={styles.searchBtn}>Pretraži</button>
            </div>

            {showForm && (
                <form onSubmit={handleSubmit} style={styles.form}>
                    <input name="firstName" placeholder="Ime" value={newPatient.firstName} onChange={handleInputChange} required style={styles.input}/>
                    <input name="lastName" placeholder="Prezime" value={newPatient.lastName} onChange={handleInputChange} required style={styles.input}/>
                    <input name="phoneNumber" placeholder="Telefon" value={newPatient.phoneNumber} onChange={handleInputChange} required style={styles.input}/>
                    <input name="birthDate" type="date" placeholder="Datum rođenja" value={newPatient.birthDate} onChange={handleInputChange} style={styles.input}/>
                    <select name="gender" value={newPatient.gender} onChange={handleInputChange} style={styles.input}>
                        <option value="">Pol (nepoznat)</option>
                        <option value="MALE">Muški</option>
                        <option value="FEMALE">Ženski</option>
                    </select>
                    <button type="submit" style={styles.saveBtn}>Sačuvaj u bazu</button>
                </form>
            )}

            <div style={styles.listWrapper}>
                {patients.length > 0 ? (
                    patients.map(p => (
                        <div
                            key={p.id}
                            style={styles.patientRow}
                            onClick={() => handleRowClick(p.id)}
                        >
                            <div style={styles.patientInfo}>
                                <div style={styles.patientName}>{p.firstName} {p.lastName}</div>
                                <div style={styles.patientSubtext}>Tel: {p.phoneNumber}{p.birthDate ? ` | Rođ: ${p.birthDate}` : ''}</div>
                            </div>
                            <button style={styles.editIconBtn} onClick={(e) => openEdit(e, p)} title="Izmeni podatke">
                                <Pencil size={16} />
                            </button>
                            <div style={styles.arrow}>❯</div>
                        </div>
                    ))
                ) : (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
                        Nema pronađenih pacijenata.
                    </div>
                )}
            </div>

            {totalPages > 1 && (
                <div style={styles.pagination}>
                    <button
                        style={{ ...styles.pageBtn, ...(page === 0 ? styles.pageBtnDisabled : {}) }}
                        onClick={() => goToPage(page - 1)}
                        disabled={page === 0}
                    >
                        ‹ Prethodna
                    </button>
                    <span style={styles.pageInfo}>Strana {page + 1} od {totalPages}</span>
                    <button
                        style={{ ...styles.pageBtn, ...(page >= totalPages - 1 ? styles.pageBtnDisabled : {}) }}
                        onClick={() => goToPage(page + 1)}
                        disabled={page >= totalPages - 1}
                    >
                        Sledeća ›
                    </button>
                </div>
            )}

            {editingPatient && (
                <div style={styles.modalBackdrop} onClick={closeEdit}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.modalTitle}>Izmeni podatke pacijenta</h3>
                        <form onSubmit={saveEdit} style={styles.editForm}>
                            <input name="firstName" placeholder="Ime" value={editForm.firstName} onChange={handleEditChange} required style={styles.input} />
                            <input name="lastName" placeholder="Prezime" value={editForm.lastName} onChange={handleEditChange} required style={styles.input} />
                            <input name="phoneNumber" placeholder="Telefon" value={editForm.phoneNumber} onChange={handleEditChange} required style={styles.input} />
                            <input name="birthDate" type="date" value={editForm.birthDate} onChange={handleEditChange} style={styles.input} />
                            <select name="gender" value={editForm.gender} onChange={handleEditChange} style={styles.input}>
                                <option value="">Pol (nepoznat)</option>
                                <option value="MALE">Muški</option>
                                <option value="FEMALE">Ženski</option>
                            </select>
                            <div style={styles.editActions}>
                                <button type="submit" style={{ ...styles.saveBtn, flex: 1 }} disabled={savingEdit}>
                                    {savingEdit ? 'Čuvanje...' : 'Sačuvaj izmene'}
                                </button>
                                <button type="button" style={styles.cancelBtn} onClick={closeEdit}>Otkaži</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '15px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
    },
    title: { margin: 0, fontSize: '24px', color: '#1f2937' },
    addBtn: {
        padding: '10px 15px',
        backgroundColor: '#27ae60',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: 'bold'
    },
    searchPanel: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: '8px',
        marginBottom: '20px',
        backgroundColor: '#fff',
        padding: '10px',
        borderRadius: '10px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    miniInput: {
        padding: '10px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        fontSize: '14px'
    },
    searchBtn: {
        padding: '10px',
        backgroundColor: '#374151',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer'
    },
    form: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px',
        padding: '15px',
        backgroundColor: '#f1f3f5',
        borderRadius: '10px',
        marginBottom: '20px'
    },
    input: { padding: '12px', border: '1px solid #ccc', borderRadius: '6px' },
    saveBtn: {
        gridColumn: 'span 2',
        padding: '12px',
        backgroundColor: '#2980b9',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontWeight: 'bold'
    },
    listWrapper: {
        backgroundColor: 'white',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    },
    patientRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '15px',
        borderBottom: '1px solid #eee',
        cursor: 'pointer',
        transition: 'background 0.2s',
        // Sprečava plavi highlight na klik kod mobilnih
        WebkitTapHighlightColor: 'transparent'
    },
    patientInfo: { display: 'flex', flexDirection: 'column', flex: 1 },
    patientName: { fontSize: '17px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' },
    patientSubtext: { fontSize: '13px', color: '#7f8c8d' },
    arrow: { color: '#bdc3c7', fontSize: '18px' },
    editIconBtn: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '32px', height: '32px', marginRight: '10px',
        border: 'none', borderRadius: '8px', backgroundColor: '#f1f5f9',
        color: '#475569', cursor: 'pointer'
    },
    modalBackdrop: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '15px'
    },
    modal: {
        backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '380px',
        padding: '20px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
    },
    modalTitle: { margin: '0 0 15px 0', fontSize: '17px', color: '#1f2937' },
    editForm: { display: 'flex', flexDirection: 'column', gap: '10px' },
    editActions: { display: 'flex', gap: '10px', marginTop: '5px' },
    cancelBtn: {
        flex: 1, padding: '12px', backgroundColor: '#e2e8f0', color: '#334155',
        border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer'
    },
    pagination: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '15px',
        marginTop: '15px'
    },
    pageBtn: {
        padding: '8px 14px',
        backgroundColor: '#374151',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '14px'
    },
    pageBtnDisabled: {
        backgroundColor: '#bdc3c7',
        cursor: 'not-allowed'
    },
    pageInfo: { fontSize: '14px', color: '#1f2937' }
};

export default PatientsList;
